import axios, { type AxiosInstance } from 'axios'
import { useHubApi } from '@/composables/tithelyHub'
import ENV from '@/constants/env'
import type {
    ContactResponse,
    ConversationMessageResponse,
    ConversationMessagesResponse,
    OrganizationResponse,
    GetConversationMessagesParams,
    GetConversationsParams,
    InboxConversationsResponse,
    GetUsageParams,
    UnreadMessageStatsResponse,
    UpdateContactPayload,
    UsageResponse,
    SendMessagePayload,
    SendMessageResponse,
    CreateBlastPayload,
    BlastResponse,
    MessagingApi
} from '@/types/composables/tithelyMessaging'

class MessagingError extends Error {
    readonly step: string
    readonly status?: number
    readonly code?: string

    constructor(step: string, message: string, status?: number, code?: string) {
        super(message)
        this.name = `MessagingError`
        this.step = step
        this.status = status
        this.code = code
    }
}

const MESSAGING_HOST = ENV.messagingHost

// Dedicated client for the Messaging service. Intentionally separate from both
// the shared Breeze axiosInstance and the Hub client — each service gets its
// own instance so interceptors and base URLs stay independent.
const messaging: AxiosInstance = axios.create({
    baseURL: MESSAGING_HOST,
    headers: { 'Content-Type': `application/json` }
})

const toMessagingError = (step: string, error: unknown): MessagingError => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status
        const data = error.response?.data as
            | { error?: string | { reason?: string; message?: string }; error_description?: string; message?: string }
            | undefined
        const errorField = data?.error
        const code = typeof errorField === `string` ? errorField : errorField?.reason
        const message =
            data?.error_description ??
            (typeof errorField === `object` ? errorField?.message : undefined) ??
            data?.message ??
            (typeof errorField === `string` ? errorField : undefined) ??
            error.message
        return new MessagingError(step, message, status, code)
    }

    return new MessagingError(step, error instanceof Error ? error.message : String(error))
}

const useMessaging = (): MessagingApi => {
    const { getConnection } = useHubApi()

    const request = async <T>(
        method: string,
        path: string,
        data?: unknown,
        params?: Record<string, unknown>
    ): Promise<T> => {
        const { accessToken } = await getConnection()
        try {
            const { data: responseData } = await messaging.request<T>({
                method,
                url: path,
                headers: { Authorization: `Bearer ${accessToken}` },
                ...(data !== undefined ? { data } : {}),
                ...(params !== undefined ? { params } : {})
            })
            return responseData
        } catch (caught) {
            throw toMessagingError(`api`, caught)
        }
    }

    const sendMessage = async (payload: SendMessagePayload): Promise<SendMessageResponse> => {
        const { organizationId } = await getConnection()
        return request<SendMessageResponse>(`POST`, `/v1/organizations/${organizationId}/messages`, payload)
    }

    const createBlast = async (payload: CreateBlastPayload): Promise<BlastResponse> => {
        const { organizationId } = await getConnection()
        return request<BlastResponse>(`POST`, `/v1/organizations/${organizationId}/blasts`, payload)
    }

    const getContact = async (mdsId: string): Promise<ContactResponse> => {
        const { organizationId } = await getConnection()
        return request<ContactResponse>(`GET`, `/v1/organizations/${organizationId}/contacts/mds/${mdsId}`)
    }

    const updateContact = async (mdsId: string, payload: UpdateContactPayload): Promise<ContactResponse> => {
        const { organizationId } = await getConnection()
        return request<ContactResponse>(`PATCH`, `/v1/organizations/${organizationId}/contacts/mds/${mdsId}`, payload)
    }

    const getConversations = async (params?: GetConversationsParams): Promise<InboxConversationsResponse> => {
        const { organizationId } = await getConnection()
        return request<InboxConversationsResponse>(
            `GET`,
            `/v1/organizations/${organizationId}/conversations`,
            null,
            params as Record<string, unknown>
        )
    }

    const getConversationMessages = async (
        mdsId: string,
        params?: GetConversationMessagesParams
    ): Promise<ConversationMessagesResponse> => {
        const { organizationId } = await getConnection()
        return request<ConversationMessagesResponse>(
            `GET`,
            `/v1/organizations/${organizationId}/conversations/mds/${mdsId}/messages`,
            null,
            params as Record<string, unknown>
        )
    }

    // Same call the Tithely UI inbox makes when a thread is opened: one toggle per message.
    const markMessageRead = async (conversationId: string, messageId: string): Promise<ConversationMessageResponse> => {
        const { organizationId } = await getConnection()
        return request<ConversationMessageResponse>(
            `PATCH`,
            `/v1/organizations/${organizationId}/conversations/${conversationId}/messages/${messageId}`,
            { data: { read: true } }
        )
    }

    const getOrganization = async (): Promise<OrganizationResponse> => {
        const { organizationId } = await getConnection()
        return request<OrganizationResponse>(`GET`, `/v1/organizations/${organizationId}`)
    }

    const getUsage = async (params: GetUsageParams): Promise<UsageResponse> => {
        const { organizationId } = await getConnection()
        return request<UsageResponse>(
            `GET`,
            `/v1/organizations/${organizationId}/usage`,
            null,
            params as unknown as Record<string, unknown>
        )
    }

    const getUnreadMessageStats = async (): Promise<UnreadMessageStatsResponse> => {
        const { organizationId } = await getConnection()
        return request<UnreadMessageStatsResponse>(`GET`, `/v1/organizations/${organizationId}/unread-messages/me`)
    }

    return {
        sendMessage,
        createBlast,
        markMessageRead,
        getContact,
        updateContact,
        getConversations,
        getConversationMessages,
        getOrganization,
        getUsage,
        getUnreadMessageStats
    }
}

export { MessagingError }
export default useMessaging
